// สูตรความจุ/พีคของเพดานต่อบัญชี — pure function ไม่แตะ React (มี capacityMath.test.ts)
// นิยามต้องตรงกับ gate ใน backend middleware/checkBankDailyLimit.js:
//   หน้าต่างเลื่อน N รายการ / S วินาที (นับ order ที่สร้าง paid+pending) · เพดานรายวัน D · ทุกเพดานที่เปิดต้องผ่าน (AND) · 0 = ปิด
import { fmtNum } from '../../../utils/formatMoney';
import { WINDOW_MAX_SECONDS, WINDOW_DEFAULT_SECONDS } from '../../../utils/bankLimit';

export interface Capacity {
  perMin: number;
  perHour: number;
  perDay: number;
}

export const SECONDS_PER = { minute: 60, hour: 3600, day: 86400 } as const;

/** ปัดเป็นจำนวนเต็มในช่วง [min, max] — ค่าเพี้ยน (NaN) → min */
export const clampInt = (v: number, min: number, max: number): number => {
  const n = Math.trunc(Number(v));
  if (!Number.isFinite(n)) return min;
  return Math.min(max, Math.max(min, n));
};

const normSeconds = (s: number): number => clampInt(s || WINDOW_DEFAULT_SECONDS, 1, WINDOW_MAX_SECONDS);

/**
 * ภายใน T วินาที หน้าต่างเลื่อน N/S รับได้มากสุด N × ⌈T/S⌉
 * (ยิง N ใบทันทีต้นทุก S วินาที — หน้าต่างย้อนหลังพ้นชุดก่อนพอดี) · N ≤ 0 = ปิด → Infinity
 */
export const windowCap = (n: number, s: number, t: number): number =>
  n <= 0 ? Infinity : n * Math.ceil(t / normSeconds(s));

/** ความจุต่อบัญชี = min(ความจุหน้าต่าง, D ถ้า D>0) · ปิดทั้งคู่ = Infinity (ไม่จำกัด) */
export const capacityFor = ({ n, s, d }: { n: number; s: number; d: number }): Capacity => {
  const capT = (t: number): number => {
    const w = windowCap(n, s, t);
    return d > 0 ? Math.min(w, d) : w;
  };
  return { perMin: capT(SECONDS_PER.minute), perHour: capT(SECONDS_PER.hour), perDay: capT(SECONDS_PER.day) };
};

export const ZERO_CAPACITY: Capacity = { perMin: 0, perHour: 0, perDay: 0 };

/** รวมหลายบัญชี — มี Infinity สักตัวผลรวมเป็น Infinity ("ไม่จำกัด") เอง */
export const sumCapacity = (caps: Capacity[]): Capacity =>
  caps.reduce(
    (acc, c) => ({ perMin: acc.perMin + c.perMin, perHour: acc.perHour + c.perHour, perDay: acc.perDay + c.perDay }),
    ZERO_CAPACITY,
  );

export interface WindowPeak {
  /** จำนวน order มากสุดที่ตกในหน้าต่างยาว S วินาทีใด ๆ */
  count: number;
  /** เวลา (ms) ของใบสุดท้ายในหน้าต่างที่พีค — null เมื่อไม่มี order */
  endMs: number | null;
}

/**
 * พีคในหน้าต่าง — two-pointer บน array เวลาสร้างที่เรียง ASC, O(n)
 * หน้าต่าง (t−S, t] ครึ่งเปิด — ต่างจาก ZCOUNT ฝั่ง backend แค่ระดับมิลลิวินาที
 */
export const peakInWindow = (sortedMs: number[], s: number): WindowPeak => {
  const win = normSeconds(s) * 1000;
  let i = 0;
  let best = 0;
  let endMs: number | null = null;
  for (let j = 0; j < sortedMs.length; j++) {
    while (sortedMs[j] - sortedMs[i] >= win) i++;
    const count = j - i + 1;
    if (count > best) {
      best = count;
      endMs = sortedMs[j];
    }
  }
  return { count: best, endMs };
};

export interface HourPeak {
  hour: number;
  count: number;
}

/** ชั่วโมงที่มี order มากสุด — null เมื่อไม่มี order เลย (เสมอกันเอาชั่วโมงแรก) */
export const peakHour = (hourly: number[]): HourPeak | null => {
  let best: HourPeak | null = null;
  for (let hour = 0; hour < hourly.length; hour++) {
    const count = hourly[hour];
    if (count > 0 && (best === null || count > best.count)) best = { hour, count };
  }
  return best;
};

/** รวม hourly ของหลายบัญชีแบบ element-wise เป็น 24 ช่อง */
export const sumHourly = (rows: number[][]): number[] => {
  const out = new Array<number>(24).fill(0);
  rows.forEach((h) => h.forEach((v, i) => { if (i < 24) out[i] += v; }));
  return out;
};

export type WindowStatus = 'free' | 'moderate' | 'near' | 'hit';

/** % ของพีคเทียบ N — null เมื่อปิดหน้าต่าง (N=0) */
export const windowUsagePct = (peak: number, n: number): number | null => (n > 0 ? (peak / n) * 100 : null);

/** <50% ว่าง · 50–79% ปานกลาง · 80–99% ใกล้เต็ม · ≥100% ชน · N=0 → null */
export const windowStatus = (peak: number, n: number): WindowStatus | null => {
  const pct = windowUsagePct(peak, n);
  if (pct === null) return null;
  if (pct >= 100) return 'hit';
  if (pct >= 80) return 'near';
  if (pct >= 50) return 'moderate';
  return 'free';
};

/** แสดงความจุ — Infinity = "ไม่จำกัด" */
export const fmtCap = (v: number): string => (Number.isFinite(v) ? fmtNum(v) : 'ไม่จำกัด');
